import { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Boxes, Box, AlertTriangle } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { useStackRecoveryStats, useRequestStackRecovery } from '../hooks'
import { useLayers } from '@/features/layers/hooks'
import { RedObjectsListDialog } from './RedObjectsListDialog'
import { formatBytes } from '../utils/formatSize'

interface RequestRecoveryDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectID: string | null
    scenario: number | null
}

export function RequestRecoveryDialog({
    open,
    onOpenChange,
    objectID,
    scenario,
}: RequestRecoveryDialogProps) {
    const { t } = useTranslation()
    const [minSourceLayer, setMinSourceLayer] = useState<number>(1)
    const [initialMinSourceLayer, setInitialMinSourceLayer] = useState<number | null>(null)
    const { data: stats, isLoading: isLoadingStats } = useStackRecoveryStats(objectID, minSourceLayer)
    const { data: layersResponse } = useLayers()
    const requestMutation = useRequestStackRecovery()
    const [recoverRedObjects, setRecoverRedObjects] = useState<'Yes' | 'No'>('No')
    const [showRedObjectsDialog, setShowRedObjectsDialog] = useState(false)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)
    const [showErrorDialog, setShowErrorDialog] = useState(false)
    const [recoveryErrorMessage, setRecoveryErrorMessage] = useState<string | null>(null)

    // Create a map of layerID to description for slider labels
    const layerLabelsMap = useMemo(() => {
        const layers = layersResponse?.data || []
        const map = new Map<number, string>()
        layers.forEach((layer) => {
            if (layer.layerID !== null && layer.layerID !== undefined) {
                map.set(layer.layerID, layer.description || `Layer ${layer.layerID}`)
            }
        })
        return map
    }, [layersResponse])

    // Create stepLabels function for the slider
    const getStepLabel = useCallback((value: number) => {
        return layerLabelsMap.get(value) || value.toString()
    }, [layerLabelsMap])

    useEffect(() => {
        if (stats && stats.minSourceLayer && initialMinSourceLayer === null) {
            setInitialMinSourceLayer(stats.minSourceLayer)
            setMinSourceLayer(stats.minSourceLayer)
        }
    }, [stats, initialMinSourceLayer])

    const handleConfirm = async () => {
        if (!objectID || !scenario) return
        setRecoveryErrorMessage(null)

        try {
            const result = await requestMutation.mutateAsync({
                objectID,
                recoverRedObjects,
                minSourceLayer,
                scenario,
            })

            if (result?.success) {
                setShowSuccessDialog(true)
            } else {
                const msg =
                    (result as { sqlMessage?: string; outcome?: string })?.sqlMessage ||
                    (result as { sqlMessage?: string; outcome?: string })?.outcome ||
                    t('objects.recoveryRequestErrorMessage')
                setRecoveryErrorMessage(msg)
                setShowErrorDialog(true)
            }
        } catch (error: unknown) {
            const msg =
                (error as { response?: { data?: { data?: { sqlMessage?: string } } } })?.response?.data?.data?.sqlMessage ||
                (error as Error)?.message ||
                t('objects.recoveryRequestErrorMessage')
            setRecoveryErrorMessage(msg)
            setShowErrorDialog(true)
        }
    }

    const handleSuccessClose = () => {
        setShowSuccessDialog(false)
        onOpenChange(false)
    }

    if (!objectID || !scenario) {
        return null
    }

    const maxLayer = stats?.maxLayerID || 10
    const minLayer = stats?.minSourceLayer || 1

    return (
        <>
            <Dialog open={open && !showSuccessDialog && !showErrorDialog} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-[700px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.requestRecovery')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.requestRecoveryDescription', { objectID: objectID || '' })}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Stats Cards - Matching ObjectListPage style */}
                        <div className="flex justify-center gap-4">
                            {/* Services Card */}
                            <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                                <div className="flex items-center space-x-2">
                                    <Boxes className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm font-medium">
                                            {t('objects.numberOfServices')}
                                        </p>
                                        <p className="text-2xl font-bold">
                                            {isLoadingStats ? '-' : (stats?.servicesCount || 0)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Objects Card */}
                            <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                                <div className="flex items-center space-x-2">
                                    <Box className="h-5 w-5 text-muted-foreground" />
                                    <div>
                                        <p className="text-sm font-medium">
                                            {t('objects.numberOfObjects')}
                                        </p>
                                        <p className="text-2xl font-bold">
                                            {isLoadingStats ? '-' : (stats?.objectsCount || 0)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Red Objects Card */}
                            <div className="rounded-lg border bg-card p-6 relative" style={{ width: 'calc((100% - 2rem) / 3)' }}>
                                <div className="flex items-center space-x-2">
                                    <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">
                                            {t('objects.redObjects')}
                                        </p>
                                        <p className="text-2xl font-bold text-red-600">
                                            {isLoadingStats ? '-' : (stats?.redObjectsCount || 0)}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="absolute bottom-4 right-4"
                                    onClick={() => setShowRedObjectsDialog(true)}
                                    disabled={!stats?.redObjectsCount || stats.redObjectsCount === 0}
                                >
                                    {t('objects.listRedObjects')}
                                </Button>
                            </div>
                        </div>

                        {/* Recover Red or Green Objects */}
                        <div className="space-y-3">
                            <Label>{t('objects.recoverObjects')}</Label>
                            <div className="flex justify-center gap-3">
                                <Button
                                    type="button"
                                    variant={recoverRedObjects === 'Yes' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setRecoverRedObjects('Yes')}
                                    className={recoverRedObjects === 'Yes' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                                >
                                    {t('objects.retrieveRedObjects')}
                                </Button>
                                <Button
                                    type="button"
                                    variant={recoverRedObjects === 'No' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setRecoverRedObjects('No')}
                                    className={recoverRedObjects === 'No' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                                >
                                    {t('objects.retrieveGreenObjects')}
                                </Button>
                            </div>
                        </div>

                        {/* Min Source Layer Slider */}
                        <div className="space-y-3">
                            <Label>{t('objects.minSourceLayer')}</Label>
                            <div className="flex justify-center px-2">
                                <Slider
                                    orientation="horizontal"
                                    min={minLayer}
                                    max={maxLayer}
                                    step={1}
                                    value={minSourceLayer}
                                    onChange={setMinSourceLayer}
                                    showSteps={true}
                                    showValue={true}
                                    trackLength={500}
                                    trackThickness={4}
                                    stepLabels={getStepLabel}
                                />
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={requestMutation.isPending}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={requestMutation.isPending || isLoadingStats}
                        >
                            {requestMutation.isPending
                                ? t('common.processing')
                                : t('objects.confirmRequestRecovery')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Red Objects List Dialog */}
            <RedObjectsListDialog
                open={showRedObjectsDialog}
                onOpenChange={setShowRedObjectsDialog}
                objectID={objectID}
            />

            {/* Success Dialog */}
            <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.recoveryRequested')}</DialogTitle>
                        <DialogDescription>
                            {t('objects.recoveryRequestedMessage', { objectID })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground">
                            {t('objects.recoveryNeedsApproval')}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button onClick={handleSuccessClose}>
                            {t('common.yes')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Error Dialog */}
            <Dialog open={showErrorDialog} onOpenChange={(open) => { setShowErrorDialog(open); if (!open) setRecoveryErrorMessage(null) }}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('objects.recoveryRequestError')}</DialogTitle>
                        <DialogDescription>
                            {recoveryErrorMessage || t('objects.recoveryRequestErrorMessage')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => { setShowErrorDialog(false); setRecoveryErrorMessage(null) }}>
                            {t('common.close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
